import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { server } from "../../server";
import { getStoredAuthToken } from "../../utils/authSession";
import ProfileGalleryPostActionIcon from "./ProfileGalleryPostActionIcon";
import ProfileGalleryPostActions from "./ProfileGalleryPostActions";

const getInitials = (name = "") => {
  const initials = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return initials || "BV";
};

const formatGalleryDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const getImageCaption = (image = {}) => {
  return String(
    image.postDescription
    || image.description
    || image.caption
    || image.comment
    || ""
  ).trim();
};

const getCount = (...values) => {
  const value = values.find((candidate) => Number.isFinite(Number(candidate)));
  return Number(value || 0);
};

const getAvatarUrl = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  return value.url || "";
};

const getImagePostId = (image = {}) => image.postId || image.sourcePostId || image.post?._id || image.post;

const getImageId = (image = {}) => image.imageId || image._id || image.id;

const getCommentName = (comment = {}) => (
  comment.name
  || comment.commenter?.name
  || comment.userId?.name
  || comment.shopId?.name
  || "MATRIX member"
);

const getCommentAvatar = (comment = {}) => getAvatarUrl(
  comment.avatar
  || comment.commenter?.avatar
  || comment.userId?.avatar
  || comment.shopId?.avatar
);

const getCommentText = (comment = {}) => String(comment.commentText || comment.text || "").trim();

const getAuthRequestConfig = () => ({
  withCredentials: true,
  headers: {
    Authorization: `Bearer ${getStoredAuthToken() || ""}`,
    "current-profile": sessionStorage.getItem("currentProfile") || "defaultProfile",
  },
});

const isShopProfileName = (value) => Boolean(
  value
  && value !== "User"
  && value !== "Guest"
  && value !== "defaultProfile"
);

const ProfileImageGalleryModal = ({
  images = [],
  currentImageIndex = 0,
  onClose,
  onPrevious,
  onNext,
  onGoToImage,
  ownerName = "MATRIX",
  ownerAvatar,
  profileType = "Profile",
  onDeleteImage,
}) => {
  const currentImage = images[currentImageIndex] || {};
  const { user } = useSelector((state) => state.user || {});
  const sellerState = useSelector((state) => state.seller || {});
  const seller = sellerState.activeSeller || sellerState.seller;
  const imageId = getImageId(currentImage);
  const postId = getImagePostId(currentImage);
  const rawCaption = getImageCaption(currentImage);
  const [postDescriptionOverride, setPostDescriptionOverride] = useState(null);
  const caption = postDescriptionOverride !== null ? postDescriptionOverride : rawCaption;
  const createdValue = currentImage.postCreatedAt || currentImage.createdAt;
  const updatedValue = currentImage.postUpdatedAt || currentImage.updatedAt;
  const postedDate = formatGalleryDate(createdValue);
  const editedDate = formatGalleryDate(updatedValue);
  const wasEdited = Boolean(
    createdValue
    && updatedValue
    && new Date(updatedValue).getTime() - new Date(createdValue).getTime() > 1000
  );
  const displayName = currentImage.postOwnerName || currentImage.ownerName || ownerName || profileType;
  const displayAvatar = currentImage.postOwnerAvatar || currentImage.ownerAvatar || ownerAvatar;
  const commentCount = getCount(currentImage.totalComments, currentImage.commentCount, currentImage.comments?.length);
  const [loadedComments, setLoadedComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentLikeState, setCommentLikeState] = useState({});
  const [commentShareState, setCommentShareState] = useState({});
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isEditingPost, setIsEditingPost] = useState(false);
  const [editedPostDescription, setEditedPostDescription] = useState(rawCaption);
  const [isSavingPost, setIsSavingPost] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [galleryActionError, setGalleryActionError] = useState("");
  const actionsMenuRef = useRef(null);
  const currentProfileName = sessionStorage.getItem("currentProfile") || profileType;
  const currentProfileType = isShopProfileName(currentProfileName) ? "Shop" : "User";
  const currentActorId = currentProfileType === "Shop" ? seller?._id : user?._id;
  const hasManageActions = Boolean(postId || imageId);
  const embeddedComments = useMemo(() => (
    Array.isArray(currentImage.comments)
      ? currentImage.comments.filter((comment) => getCommentText(comment))
      : []
  ), [currentImage.comments]);
  const galleryComments = postId ? loadedComments : embeddedComments;
  const hasGalleryComments = galleryComments.length > 0;
  const shouldShowDiscussion = Boolean(caption || postId || hasGalleryComments || isLoadingComments || hasManageActions || isEditingPost);

  useEffect(() => {
    setPostDescriptionOverride(null);
    setEditedPostDescription(rawCaption);
    setIsEditingPost(false);
    setIsActionsMenuOpen(false);
    setGalleryActionError("");
  }, [currentImage._id, currentImage.imageId, currentImage.url, postId, rawCaption]);

  useEffect(() => {
    setLoadedComments([]);
    setCommentLikeState({});
    setCommentShareState({});

    if (!postId || commentCount === 0) {
      return;
    }

    let isMounted = true;
    setIsLoadingComments(true);

    axios.get(`${server}/posts/get-comments/${postId}?page=1&limit=20`, getAuthRequestConfig())
      .then((response) => {
        if (!isMounted) {
          return;
        }

        const comments = Array.isArray(response.data?.comments) ? response.data.comments : [];
        setLoadedComments(comments.filter((comment) => getCommentText(comment)));
      })
      .catch(() => {
        if (isMounted) {
          setLoadedComments([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingComments(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [postId, commentCount]);

  useEffect(() => {
    const nextLikeState = {};
    galleryComments.forEach((comment) => {
      if (comment._id) {
        nextLikeState[comment._id] = {
          isLiked: Boolean(comment.isLikedByCurrentUser),
          likeCount: getCount(comment.likeCount, comment.likes?.length),
        };
      }
    });
    setCommentLikeState(nextLikeState);
  }, [galleryComments]);

  useEffect(() => {
    if (!isActionsMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target)) {
        setIsActionsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isActionsMenuOpen]);

  const handleLikeComment = async (comment) => {
    if (!postId || !comment?._id || !currentActorId) {
      return;
    }

    const currentState = commentLikeState[comment._id] || {
      isLiked: Boolean(comment.isLikedByCurrentUser),
      likeCount: getCount(comment.likeCount, comment.likes?.length),
    };
    const nextState = {
      isLiked: !currentState.isLiked,
      likeCount: Math.max(0, currentState.likeCount + (currentState.isLiked ? -1 : 1)),
    };

    setCommentLikeState((state) => ({ ...state, [comment._id]: nextState }));

    try {
      await axios.patch(`${server}/like/comments/${comment._id}/like`, {
        likedBy: currentActorId,
        profileType: currentProfileType,
      }, getAuthRequestConfig());
    } catch (error) {
      setCommentLikeState((state) => ({ ...state, [comment._id]: currentState }));
    }
  };

  const handleShareComment = async (comment) => {
    const commentText = getCommentText(comment);

    if (!postId || !commentText || !currentActorId) {
      return;
    }

    setCommentShareState((state) => ({ ...state, [comment._id]: "sharing" }));

    try {
      await axios.post(`${server}/posts/share-post/${postId}`, {
        profileType: currentProfileType,
        reshareDescription: commentText,
      }, getAuthRequestConfig());
      setCommentShareState((state) => ({ ...state, [comment._id]: "shared" }));
    } catch (error) {
      setCommentShareState((state) => ({ ...state, [comment._id]: "error" }));
    }
  };

  const handleStartEditPost = () => {
    if (!postId) {
      return;
    }

    setEditedPostDescription(caption);
    setGalleryActionError("");
    setIsEditingPost(true);
    setIsActionsMenuOpen(false);
  };

  const handleSavePost = async (event) => {
    event.preventDefault();

    if (!postId) {
      return;
    }

    setIsSavingPost(true);
    setGalleryActionError("");

    try {
      const response = await axios.patch(`${server}/posts/edit-post/${postId}`, {
        description: editedPostDescription.trim(),
      }, getAuthRequestConfig());
      setPostDescriptionOverride(response.data?.description ?? editedPostDescription.trim());
      setIsEditingPost(false);
    } catch (error) {
      setGalleryActionError(error.response?.data?.message || "Unable to edit this post.");
    } finally {
      setIsSavingPost(false);
    }
  };

  const handleDeletePost = async () => {
    if (!postId || !window.confirm("Delete this post?")) {
      return;
    }

    setIsDeleting(true);
    setGalleryActionError("");
    setIsActionsMenuOpen(false);

    try {
      await axios.delete(`${server}/posts/delete-post/${postId}`, getAuthRequestConfig());
      onClose?.();
    } catch (error) {
      setGalleryActionError(error.response?.data?.message || "Unable to delete this post.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!imageId || !window.confirm("Delete this photo from your gallery?")) {
      return;
    }

    setIsDeleting(true);
    setGalleryActionError("");
    setIsActionsMenuOpen(false);

    try {
      await axios.delete(`${server}/image/${imageId}`, getAuthRequestConfig());
      onDeleteImage?.(imageId);
      onClose?.();
    } catch (error) {
      setGalleryActionError(error.response?.data?.message || "Unable to delete this photo.");
    } finally {
      setIsDeleting(false);
    }
  };

  const modal = (
    <div className="my-images-component-modal-overlay profile-image-gallery-modal-overlay" onClick={onClose}>
      <div
        className="my-images-component-modal-content profile-image-gallery-modal-content"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="my-images-component-close-button profile-image-gallery-close-button"
          onClick={onClose}
          aria-label="Close gallery"
        >
          &times;
        </button>

        <article className={`profile-image-gallery-post-shell ${!shouldShowDiscussion ? "profile-image-gallery-post-shell--media-only" : ""}`}>
          <header className="profile-image-gallery-post-header">
            <div className="profile-image-gallery-avatar-pill">
              {displayAvatar ? (
                <img src={displayAvatar} alt="" className="profile-image-gallery-avatar" />
              ) : (
                <span className="profile-image-gallery-avatar profile-image-gallery-avatar-fallback">
                  {getInitials(displayName)}
                </span>
              )}
              <span className="profile-image-gallery-author-meta">
                <span className="profile-image-gallery-author-name">{displayName}</span>
                <span className="profile-image-gallery-date-line">
                  {postedDate || "Photo"}
                  {wasEdited && <span>Edited {editedDate}</span>}
                </span>
              </span>
            </div>
          </header>

          <div className="my-images-component-gallery profile-image-gallery-stage">
            <button
              type="button"
              className="my-images-component-nav-button my-images-component-nav-button-left"
              onClick={onPrevious}
              aria-label="Previous image"
            >
              &#10094;
            </button>
            <img
              src={currentImage.url}
              alt={`Gallery ${currentImageIndex + 1}`}
              className="my-images-component-gallery-image profile-image-gallery-image"
            />
            <button
              type="button"
              className="my-images-component-nav-button my-images-component-nav-button-right"
              onClick={onNext}
              aria-label="Next image"
            >
              &#10095;
            </button>
          </div>

          <ProfileGalleryPostActions
            image={currentImage}
            ownerName={ownerName}
            ownerAvatar={ownerAvatar}
            profileType={profileType}
            className="profile-image-gallery-engagement-row"
            buttonClassName="profile-image-gallery-engagement-button"
            ariaLabel="Photo engagement"
          />

          {shouldShowDiscussion && (
            <aside className="profile-image-gallery-discussion-panel" aria-label="Photo post and comments">
              {hasManageActions && (
                <div className="profile-image-gallery-discussion-toolbar" ref={actionsMenuRef}>
                  <span>{postId ? "Post actions" : "Photo actions"}</span>
                  <button
                    type="button"
                    className="profile-image-gallery-discussion-menu-button"
                    onClick={() => setIsActionsMenuOpen((currentIsOpen) => !currentIsOpen)}
                    aria-label="Open photo actions"
                    aria-expanded={isActionsMenuOpen}
                  >
                    &#8942;
                  </button>
                  {isActionsMenuOpen && (
                    <div className="profile-image-gallery-discussion-menu" role="menu">
                      {postId && (
                        <>
                          <button type="button" role="menuitem" onClick={handleStartEditPost}>Edit Post</button>
                          <button type="button" role="menuitem" onClick={handleDeletePost} disabled={isDeleting}>Delete Post</button>
                          <button type="button" role="menuitem" onClick={() => window.location.assign(`/post/${postId}`)}>Open Post</button>
                        </>
                      )}
                      {imageId && <button type="button" role="menuitem" onClick={handleDeleteImage} disabled={isDeleting}>Delete Photo</button>}
                    </div>
                  )}
                </div>
              )}

              {isEditingPost && (
                <form className="profile-image-gallery-edit-post-form" onSubmit={handleSavePost}>
                  <textarea
                    value={editedPostDescription}
                    onChange={(event) => setEditedPostDescription(event.target.value)}
                    placeholder="Edit original post text..."
                    rows={4}
                  />
                  <div>
                    <button type="button" onClick={() => setIsEditingPost(false)}>Cancel</button>
                    <button type="submit" disabled={isSavingPost}>{isSavingPost ? "Saving..." : "Save Post"}</button>
                  </div>
                </form>
              )}

              {galleryActionError && <p className="profile-image-gallery-action-error">{galleryActionError}</p>}

              {caption && (
                <div className="profile-image-gallery-thought-card post-widget-reshare">
                  <div className="post-widget-reshare-header">
                    {displayAvatar ? (
                      <img src={displayAvatar} alt="" className="post-widget-reshare-avatar" />
                    ) : (
                      <span className="post-widget-reshare-avatar profile-image-gallery-avatar-fallback">
                        {getInitials(displayName)}
                      </span>
                    )}
                    <span className="post-widget-reshare-by">
                      <span className="post-widget-reshare-label">Original thought</span>
                      <span className="post-widget-reshare-name">{displayName}</span>
                    </span>
                  </div>
                  <p className="post-widget-reshare-description">{caption}</p>
                </div>
              )}

              {postId && (
                <div className="profile-image-gallery-discussion-actions">
                  <button
                    type="button"
                    className="profile-image-gallery-discussion-button"
                    onClick={() => window.location.assign(`/post/${postId}`)}
                  >
                    Open Post
                  </button>
                  <button
                    type="button"
                    className="profile-image-gallery-discussion-button profile-image-gallery-discussion-button-secondary"
                    onClick={() => window.location.assign(`/post/${postId}`)}
                  >
                    {commentCount} Comment{commentCount === 1 ? "" : "s"}
                  </button>
                </div>
              )}

              {(isLoadingComments || hasGalleryComments) && (
                <div className="profile-image-gallery-comments-list" aria-label="Photo comments">
                  {isLoadingComments ? (
                    <p className="profile-image-gallery-comments-loading">Loading comments...</p>
                  ) : galleryComments.map((comment) => {
                    const commentText = getCommentText(comment);
                    const commentAvatar = getCommentAvatar(comment);
                    const commentName = getCommentName(comment);
                    const commentState = commentLikeState[comment._id] || {
                      isLiked: Boolean(comment.isLikedByCurrentUser),
                      likeCount: getCount(comment.likeCount, comment.likes?.length),
                    };
                    const shareState = commentShareState[comment._id];

                    return (
                      <article className="profile-image-gallery-comment-card" key={comment._id || `${commentName}-${comment.createdAt}`}>
                        {commentAvatar ? (
                          <img src={commentAvatar} alt="" className="profile-image-gallery-comment-avatar" />
                        ) : (
                          <span className="profile-image-gallery-comment-avatar profile-image-gallery-comment-avatar-fallback">
                            {String(commentName || "BV").charAt(0).toUpperCase()}
                          </span>
                        )}
                        <div className="profile-image-gallery-comment-body">
                          <strong>{commentName}</strong>
                          <p>{commentText}</p>
                          <div className="profile-image-gallery-comment-actions">
                            <button type="button" onClick={() => handleLikeComment(comment)} disabled={!postId || !currentActorId}>
                              <ProfileGalleryPostActionIcon type="like" active={commentState.isLiked || commentState.likeCount > 0} />
                              <span>{commentState.likeCount}</span>
                            </button>
                            <button type="button" onClick={() => postId && window.location.assign(`/post/${postId}`)} disabled={!postId}>
                              <ProfileGalleryPostActionIcon type="comment" />
                              <span>Comment</span>
                            </button>
                            <button type="button" onClick={() => handleShareComment(comment)} disabled={!postId || !currentActorId || shareState === "sharing"}>
                              <ProfileGalleryPostActionIcon type="share" />
                              <span>{shareState === "sharing" ? "Sharing" : shareState === "shared" ? "Shared" : "Share"}</span>
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </aside>
          )}
        </article>

        <div className="my-images-component-thumbnail-navigation profile-image-gallery-thumbnail-navigation">
          {images.map((image, index) => (
            <img
              key={image._id || image.url || index}
              src={image.url}
              alt={`Thumbnail ${index + 1}`}
              className={`my-images-component-thumbnail-small ${currentImageIndex === index ? "active" : ""}`}
              onClick={() => onGoToImage(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
};

export default ProfileImageGalleryModal;